import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import ErrorMsg from "./msg/ErrorMsg";
import SuccsMsg from "./msg/SuccsMsg";
import { Navigate } from "react-router-dom";
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton,
    styled, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow, TextField, Typography,
    Paper, InputAdornment
} from "@mui/material";
import Appbar from "./Appbar";
import MenuList from "./MenuList";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
        fontSize: 16,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 15,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
        backgroundColor: '#e3f2fd',
        transition: 'background-color 0.2s ease',
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const ManageProduct = () => {
    const [loading, setLoading] = useState(true);
    const [productList, setProductList] = useState([]);
    const [errorMsg, setErrorMsg] = useState("");
    const [succsMsg, setSuccsMsg] = useState("");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // state جدید برای强制 رفرش لیست بعد از عملیات
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // استفاده از useRef برای جلوگیری از درخواست‌های همزمان
    const abortControllerRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    // دیالوگ ویرایش
    const [dialogEdit, setDialogEdit] = useState(false);
    const [originalId, setOriginalId] = useState("");
    const [editId, setEditId] = useState("");
    const [editName, setEditName] = useState("");
    const [editStock, setEditStock] = useState("");
    const [editStockinway, setEditStockinway] = useState("");
    const [editCode, setEditCode] = useState("");

    // دیالوگ حذف
    const [dialogDelete, setDialogDelete] = useState(false);
    const [deleteId, setDeleteId] = useState("");
    const [deleteName, setDeleteName] = useState("");

    // دیالوگ‌های جدید برای صفر کردن موجودی
    const [dialogResetStock, setDialogResetStock] = useState(false);
    const [dialogResetStockInWay, setDialogResetStockInWay] = useState(false);

    // دیباونس برای جستجو
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [search]);

    // دریافت لیست محصولات - وابسته به debouncedSearch و refreshTrigger
    useEffect(() => {
        const fetchProducts = async () => {
            // اگر درخواست قبلی در حال انجام است، آن را کنسل کن
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // ایجاد AbortController جدید
            abortControllerRef.current = new AbortController();

            setLoading(true);

            try {
                const data = {
                    token: localStorage.getItem("adminlogintoken"),
                    search: debouncedSearch,
                    limitzero: false
                };

                const response = await axios.post("/api/get_productlist.php", data, {
                    signal: abortControllerRef.current.signal
                });

                setLoading(false);

                if (response.status === 200) {
                    if (response.data.error === false) {
                        setProductList(response.data.data || []);
                    } else {
                        localStorage.clear();
                    }
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    setLoading(false);
                    console.error("Error fetching products:", error);
                }
            }
        };

        if (localStorage.getItem("adminlogintoken")) {
            fetchProducts();
        }

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [debouncedSearch, refreshTrigger]); // اضافه کردن refreshTrigger به وابستگی‌ها

    const Redirect = () => {
        return (<Navigate replace to="/login" />);
    };

    // ذخیره تغییرات
    const handle_btn_save = async () => {
        if (!editId || !editName) {
            setErrorMsg("کد کالا و نام کالا نمی توانند خالی باشند");
            return;
        }

        setLoading(true);

        try {
            const data = {
                token: localStorage.getItem("adminlogintoken"),
                originalid: originalId,
                newproductid: editId,
                name: editName,
                stock: String(editStock).replace(/,/g, ''),
                stockinway: String(editStockinway).replace(/,/g, ''),
                code: editCode,
            };

            const response = await axios.post("/api/updateproductfull.php", data);
            setLoading(false);

            if (response.status === 200) {
                if (response.data.error === false) {
                    setDialogEdit(false);
                    setSuccsMsg(response.data.message);
                    // افزایش refreshTrigger برای اجبار به رفرش لیست
                    setRefreshTrigger(prev => prev + 1);
                } else {
                    setErrorMsg(response.data.message);
                }
            }
        } catch (error) {
            setLoading(false);
            setErrorMsg("خطا در ارتباط با سرور");
        }
    };

    // حذف محصول
    const handle_btn_delete = async () => {
        setLoading(true);

        try {
            const data = {
                token: localStorage.getItem("adminlogintoken"),
                productid: deleteId,
            };

            const response = await axios.post("/api/deleteproduct.php", data);
            setLoading(false);

            if (response.status === 200) {
                if (response.data.error === false) {
                    setDialogDelete(false);
                    setSuccsMsg(response.data.message);
                    // افزایش refreshTrigger برای اجبار به رفرش لیست
                    setRefreshTrigger(prev => prev + 1);
                } else {
                    setErrorMsg(response.data.message);
                }
            }
        } catch (error) {
            setLoading(false);
            setErrorMsg("خطا در ارتباط با سرور");
        }
    };

    // صفر کردن موجودی‌ها
    const handleResetStock = async () => {
        setLoading(true);
        setDialogResetStock(false);

        try {
            const data = {
                token: localStorage.getItem("adminlogintoken"),
                type: "stock"
            };

            const response = await axios.post("/api/reset_stock.php", data);
            setLoading(false);

            if (response.status === 200) {
                if (response.data.error === false) {
                    setSuccsMsg(response.data.message);
                    // افزایش refreshTrigger برای اجبار به رفرش لیست
                    setRefreshTrigger(prev => prev + 1);
                } else {
                    setErrorMsg(response.data.message);
                }
            }
        } catch (error) {
            setLoading(false);
            setErrorMsg("خطا در ارتباط با سرور");
        }
    };

    // صفر کردن موجودی‌های در راه
    const handleResetStockInWay = async () => {
        setLoading(true);
        setDialogResetStockInWay(false);

        try {
            const data = {
                token: localStorage.getItem("adminlogintoken"),
                type: "stockinway"
            };

            const response = await axios.post("/api/reset_stock.php", data);
            setLoading(false);

            if (response.status === 200) {
                if (response.data.error === false) {
                    setSuccsMsg(response.data.message);
                    // افزایش refreshTrigger برای اجبار به رفرش لیست
                    setRefreshTrigger(prev => prev + 1);
                } else {
                    setErrorMsg(response.data.message);
                }
            }
        } catch (error) {
            setLoading(false);
            setErrorMsg("خطا در ارتباط با سرور");
        }
    };

    // باز کردن دیالوگ ویرایش
    const openEditDialog = (product) => {
        setOriginalId(product.id);
        setEditId(product.id);
        setEditName(product.name);
        setEditStock(product.stock);
        setEditStockinway(product.stockinway);
        setEditCode(product.productcode || "");
        setDialogEdit(true);
    };

    // باز کردن دیالوگ حذف
    const openDeleteDialog = (id, name) => {
        setDeleteId(id);
        setDeleteName(name);
        setDialogDelete(true);
    };

    // تبدیل اعداد فارسی به انگلیسی
    const toEnglishDigits = (str) => {
        if (!str) return "";
        const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
        const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

        let result = String(str);
        persianDigits.forEach((digit, index) => {
            result = result.replace(new RegExp(digit, 'g'), index);
        });
        arabicDigits.forEach((digit, index) => {
            result = result.replace(new RegExp(digit, 'g'), index);
        });
        return result;
    };

    // فرمت عدد با جداکننده هزارگان
    const number_format = (number) => {
        if (!number && number !== 0) return "";
        const num = toEnglishDigits(String(number)).replace(/,/g, '');
        if (isNaN(num)) return number;
        return new Intl.NumberFormat("en-US").format(num);
    };

    return (
        <div>
            {!localStorage.getItem("adminlogintoken") && <Redirect />}
            {errorMsg && <ErrorMsg message={errorMsg} openn={true} seterrormsg={setErrorMsg} />}
            {succsMsg && <SuccsMsg message={succsMsg} openn={true} setsuccsmsg={setSuccsMsg} />}

            <Grid container spacing={2} sx={{ marginTop: 0 }}>
                <Grid item xs={12} md={10} sx={{ backgroundColor: "#ffffff", minHeight: "100vh" }}>
                    <Appbar />

                    <Box sx={{
                        padding: 2,
                        height: 'calc(100vh - 80px)',
                        display: 'flex',
                        flexDirection: 'column'
                    }} dir={"rtl"}>

                        {/* فیلد جستجو */}
                        <TextField
                            fullWidth
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="جستجو بر اساس نام کالا..."
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* جدول که تمام فضای موجود را می‌گیرد */}
                        <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell align="right">کد کالا</StyledTableCell>
                                        <StyledTableCell align="right">نام کالا</StyledTableCell>
                                        <StyledTableCell align="right">موجودی</StyledTableCell>
                                        <StyledTableCell align="right">موجودی در راه</StyledTableCell>
                                        <StyledTableCell align="right">فروش امروز</StyledTableCell>
                                        <StyledTableCell align="right">مانده</StyledTableCell>
                                        <StyledTableCell align="center" colSpan={2}>عملیات</StyledTableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {productList.length > 0 ? (
                                        productList.map((list) => (
                                            <StyledTableRow
                                                key={list.id}
                                                sx={{
                                                    cursor: 'default',
                                                    '&:hover': {
                                                        backgroundColor: '#e3f2fd',
                                                    }
                                                }}
                                            >
                                                <StyledTableCell align="right">{list.id}</StyledTableCell>
                                                <StyledTableCell align="right">{list.name}</StyledTableCell>
                                                <StyledTableCell
                                                    align="right"
                                                    sx={{
                                                        backgroundColor: list.stock < 0 ? "#fc9494" : "inherit",
                                                        direction: "ltr"
                                                    }}
                                                >
                                                    {number_format(list.stock)}
                                                </StyledTableCell>
                                                <StyledTableCell
                                                    align="right"
                                                    sx={{
                                                        backgroundColor: list.stockinway < 0 ? "#fc9494" : "inherit",
                                                        direction: "ltr"
                                                    }}
                                                >
                                                    {number_format(list.stockinway)}
                                                </StyledTableCell>
                                                <StyledTableCell align="right" sx={{ direction: "ltr" }}>
                                                    {number_format(list.todaysalle || 0)}
                                                </StyledTableCell>
                                                <StyledTableCell
                                                    align="right"
                                                    sx={{
                                                        backgroundColor: (list.remained || 0) < 0 ? "#fc9494" : "inherit",
                                                        direction: "ltr"
                                                    }}
                                                >
                                                    {number_format(list.remained || 0)}
                                                </StyledTableCell>
                                                <StyledTableCell align="center" sx={{ width: 50 }}>
                                                    <IconButton
                                                        onClick={() => openEditDialog(list)}
                                                        size="small"
                                                        color="primary"
                                                        title="ویرایش"
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </StyledTableCell>
                                                <StyledTableCell align="center" sx={{ width: 50 }}>
                                                    <IconButton
                                                        onClick={() => openDeleteDialog(list.id, list.name)}
                                                        size="small"
                                                        color="error"
                                                        title="حذف"
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))
                                    ) : (
                                        <StyledTableRow>
                                            <StyledTableCell colSpan={8} align="center">
                                                {loading ? "در حال بارگذاری..." : "هیچ محصولی یافت نشد"}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* دکمه‌ها در وسط زیر جدول */}
                        <Box sx={{
                            mt: 2,
                            display: 'flex',
                            gap: 3,
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<ClearAllIcon />}
                                onClick={() => setDialogResetStock(true)}
                                sx={{ minWidth: 220, gap: 1.5 }}
                            >
                                صفر کردن موجودی‌ها
                            </Button>

                            <Button
                                variant="contained"
                                color="info"
                                startIcon={<LocalShippingIcon />}
                                onClick={() => setDialogResetStockInWay(true)}
                                sx={{ minWidth: 220, gap: 1.5 }}
                            >
                                صفر کردن موجودی‌های در راه
                            </Button>
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={0} md={2} sx={{
                    backgroundColor: '#2b2b2b',
                    minHeight: "100vh",
                    display: { xs: "none", md: "block" }
                }}>
                    <MenuList />
                </Grid>
            </Grid>

            {/* دیالوگ ویرایش */}
            <Dialog open={dialogEdit} onClose={() => setDialogEdit(false)} maxWidth="sm" fullWidth>
                <DialogTitle dir="rtl">ویرایش کالا</DialogTitle>
                <DialogContent>
                    <Box dir="rtl" sx={{ pt: 1 }}>
                        <Typography variant="body2" sx={{ mt: 1, mb: 1, textAlign: "right" }}>
                            کد کالا
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={editId}
                            onChange={(e) => setEditId(e.target.value)}
                            variant="outlined"
                            dir="ltr"
                        />

                        <Typography variant="body2" sx={{ mt: 2, mb: 1, textAlign: "right" }}>
                            نام کالا
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            variant="outlined"
                        />

                        <Typography variant="body2" sx={{ mt: 2, mb: 1, textAlign: "right" }}>
                            موجودی (کیلوگرم)
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={editStock}
                            onChange={(e) => setEditStock(number_format(e.target.value))}
                            variant="outlined"
                            dir="ltr"
                        />

                        <Typography variant="body2" sx={{ mt: 2, mb: 1, textAlign: "right" }}>
                            موجودی در راه (کیلوگرم)
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={editStockinway}
                            onChange={(e) => setEditStockinway(number_format(e.target.value))}
                            variant="outlined"
                            dir="ltr"
                        />

                        <Typography variant="body2" sx={{ mt: 2, mb: 1, textAlign: "right" }}>
                            کد شناور
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={editCode}
                            onChange={(e) => setEditCode(e.target.value)}
                            variant="outlined"
                            dir="ltr"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogEdit(false)} color="inherit">
                        انصراف
                    </Button>
                    <Button onClick={handle_btn_save} color="primary" variant="contained">
                        ذخیره
                    </Button>
                </DialogActions>
            </Dialog>

            {/* دیالوگ حذف */}
            <Dialog open={dialogDelete} onClose={() => setDialogDelete(false)} maxWidth="xs" fullWidth>
                <DialogTitle dir="rtl">حذف محصول</DialogTitle>
                <DialogContent>
                    <Box dir="rtl" sx={{ pt: 1 }}>
                        <Typography>
                            آیا از حذف محصول "{deleteName}" اطمینان دارید؟
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogDelete(false)} color="inherit">
                        انصراف
                    </Button>
                    <Button onClick={handle_btn_delete} color="error" variant="contained">
                        حذف
                    </Button>
                </DialogActions>
            </Dialog>

            {/* دیالوگ تأیید صفر کردن موجودی */}
            <Dialog open={dialogResetStock} onClose={() => setDialogResetStock(false)} maxWidth="xs" fullWidth>
                <DialogTitle dir="rtl" sx={{ bgcolor: 'warning.main', color: 'white' }}>
                    هشدار
                </DialogTitle>
                <DialogContent>
                    <Box dir="rtl" sx={{ pt: 2 }}>
                        <Typography>
                            آیا مطمئن هستید تمامی موجودی‌ها صفر شود؟
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogResetStock(false)} color="inherit">
                        انصراف
                    </Button>
                    <Button onClick={handleResetStock} color="warning" variant="contained">
                        بله
                    </Button>
                </DialogActions>
            </Dialog>

            {/* دیالوگ تأیید صفر کردن موجودی در راه */}
            <Dialog open={dialogResetStockInWay} onClose={() => setDialogResetStockInWay(false)} maxWidth="xs" fullWidth>
                <DialogTitle dir="rtl" sx={{ bgcolor: 'info.main', color: 'white' }}>
                    هشدار
                </DialogTitle>
                <DialogContent>
                    <Box dir="rtl" sx={{ pt: 2 }}>
                        <Typography>
                            آیا مطمئن هستید تمامی موجودی‌های در راه صفر شود؟
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogResetStockInWay(false)} color="inherit">
                        انصراف
                    </Button>
                    <Button onClick={handleResetStockInWay} color="info" variant="contained">
                        بله
                    </Button>
                </DialogActions>
            </Dialog>

            {/* دیالوگ لودینگ */}
            <Dialog open={loading} scroll="paper">
                <CircularProgress sx={{ margin: 2 }} />
            </Dialog>
        </div>
    );
};

export default ManageProduct;